import {
  actionsKeys,
  formatAmount,
  invoiceActionStatus,
  permissionActions,
} from "@/lib/utils";
import {
  setActivityDashboardOpenTab,
  setCommunityData,
  setCurrentFinanceYear,
  setGls,
  setInvoiceDueDate,
  setInvoiceFrom,
  setInvoiceIds,
  setPaymentIds,
} from "@/redux/slices";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import {
  useGetBillingStat,
  useGetClearingStat,
  useGetCommunities,
  useGetParkDocStatusCountByCommunityId,
  useGetPaymentStat,
} from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import {
  FileChartColumn,
  FileChartColumnIncreasing,
  FileClock,
  FileCog,
  FileText,
  History,
  MailOpen,
  RefreshCw,
  Scale,
  Send,
  Settings,
} from "lucide-react";
import moment from "moment";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "@/components/Toast";
import { AUDIT_TRAIL, BILLING, CLEARING, COLLECTION, COST, GL, INVOICE, PAYMENT } from "./consts";

const useActivityDashboard = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [communityId, setCommunityId] = useState("");
  const [openTabs, setOpenTabs] = useState<string[]>([INVOICE]);
  const [year, setYear] = useState(moment().format("YYYY"));
  const [communityKey, setCommunityKey] = useState(Math.random() * 3);
  const [yearKey, setYearKey] = useState(Math.random() * 3);
  const accessPermission = useSelector(
    (state: RootState) => state.currentData.accessPermission
  );

  const [invoiceCards, setInvoiceCards] = useState([
    {
      id: "create",
      title: "Create Invoice",
      link: `${routes.invoice}/${routes.add}`,
      module: actionsKeys?.INVOICE?.toLowerCase(),
      accessKeys: permissionActions.CREATE,
    },
    {
      id: "submit_for_approval",
      count: 0,
      desc: "Invoices awaiting Submission for Approval",
      title: "Submit for Approval",
      icon: Send,
      link: `${routes.invoice}/${invoiceActionStatus.draft}`, // park
      subtitle: "Parked Documents",
      subtitleColor: "text-yellow-600",
      apiKey: "draft",
      module: actionsKeys?.INVOICE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "followup_for_approval",
      count: 0,
      desc: "Invoices requires follow-up for Approval",
      title: "Follow-up for Board Approval",
      icon: FileClock,
      link: `${routes.invoice}/${invoiceActionStatus.pending_for_approval}`, // pending for approval
      subtitle: "Pending Approval",
      subtitleColor: "text-blue-600",
      apiKey: "pendingForApproval",
      module: actionsKeys?.INVOICE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "cam_approval",
      count: 0,
      desc: "Invoices pending for CAM Approval",
      title: "Pending for CAM Approval",
      icon: FileClock,
      link: `${routes.invoice}/${invoiceActionStatus.pending_for_cam_approval}`, // admin assign data
      subtitle: "Pending Approval",
      subtitleColor: "text-blue-600",
      apiKey: "pendingForPMApproval",
      module: actionsKeys?.INVOICE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "post_invoice",
      count: 0,
      desc: "Invoices ready for Posting",
      title: "Ready for Posting",
      icon: MailOpen,
      link: `${routes.invoice}/${invoiceActionStatus.approved}`,
      subtitle: "",
      subtitleColor: "text-yellow-600",
      apiKey: "approved",
      module: actionsKeys?.INVOICE?.toLowerCase(),
      accessKeys: permissionActions.READ_POSTED,
    },
    {
      id: "posted_invoice",
      count: 0,
      desc: "Invoices Posted",
      title: "Posted Invoices",
      icon: MailOpen,
      link: `${routes.invoice}/${invoiceActionStatus.posted}`,
      subtitle: "Posted Documents",
      subtitleColor: "text-green-600",
      apiKey: "postedInvoices",
      module: actionsKeys?.INVOICE?.toLowerCase(),
      accessKeys: permissionActions.READ_POSTED,
    },
    {
      id: "summary",
      data: [
        { label: "# Total", value: 0, apiKey: "total" },
        { label: "# Parked", value: 0, apiKey: "draft" },
        { label: "# Pending Approval", value: 0, apiKey: "pendingForApproval" },
        { label: "# Approved", value: 0, apiKey: "approved" },
        { label: "# Posted", value: 0, apiKey: "postedInvoices" },
      ],
      title: "All Invoices",
      link: `${routes.invoice}/${invoiceActionStatus.all}`,
      subtitle: "",
      subtitleColor: "text-yellow-600",
      module: actionsKeys?.INVOICE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "reports",
      desc: "Invoice 360 Report",
      title: "Reports",
      icon: FileChartColumn,
      link: `${routes.invoiceReports}`,
      subtitle: "",
      subtitleColor: "text-green-600",
      apiKey: "",
      hideCount: true,
      module: actionsKeys?.INVOICE_REPORT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
  ]);

  const [paymentCards, setPaymentCards] = useState([
    {
      id: "create",
      title: "Create Payment",
      link: `${routes.payment}/${routes.add}`,
      module: actionsKeys?.PAYMENT?.toLowerCase(),
      accessKeys: permissionActions.CREATE,
    },
    {
      id: "park_payment",
      count: 0,
      desc: "Payment submit for Posting",
      title: "Submit for Posting",
      icon: FileText,
      link: `${routes.payment}/park`,
      subtitle: "Parked Documents",
      subtitleColor: "text-yellow-600",
      apiKey: "vpApproved",
      onlyCount: true,
      module: actionsKeys?.PAYMENT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "summary",
      data: [
        {
          label: "Posted Amount",
          value: 0,
          apiKey: "postedInvoiceAmount",
        },
        { label: "Paid Amount", value: 0, apiKey: "paidInvoiceAmount" },
        { label: "Pending Amount", value: 0, apiKey: "unpaidInvoiceAmount" },
        // { label: "Reversed Amount", value: 0, apiKey: "reversedInvoiceAmount" },
      ],
      title: "Posted Payment",
      link: `${routes.payment}/post`,
      module: actionsKeys?.PAYMENT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "reports",
      desc: "Payment 360 Report",
      title: "View Reports",
      icon: FileChartColumn,
      link: `${routes.payment}/${routes.reports}`,
      subtitle: "",
      subtitleColor: "text-green-600",
      apiKey: "",
      hideCount: true,
      module: actionsKeys?.PAYMENT_REPORT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "reports",
      desc: "Tolerance Report",
      title: "View Reports",
      icon: FileChartColumn,
      link: `${routes.payment}/${routes.tolerance}`,
      subtitle: "",
      subtitleColor: "text-green-600",
      apiKey: "",
      hideCount: true,
      module: actionsKeys?.TOLERANCE_REPORT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "vendor-trial-balance",
      title: "View",
      desc: "Vendor Trial Balance",
      icon: Scale,
      link: `${routes.vendorTrialBalance}`,
      subtitle: "",
      hideCount: true,
      apiKey: "draft",
      module: actionsKeys.VENDOR_TRIAL_BALANCE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "report-by-vendor",
      title: "View",
      desc: "Report By Vendor",
      icon: FileChartColumnIncreasing,
      link: `${routes.reportByVendor}`,
      subtitle: "",
      hideCount: true,
      apiKey: "",
      module: actionsKeys?.VENDOR_BALANCE_REPORT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
  ]);

  const [billingCards, setBillingCards] = useState([
    {
      id: "summary",
      data: [
        {
          label: "#Monthly HOA Fees",
          value: 0,
          apiKey: "monthly",
        },
        {
          label: "#Yearly HOA Fees",
          value: 0,
          apiKey: "yearly",
        },
        {
          label: "#Adhoc HOA Fees",
          value: 0,
          apiKey: "adhoc",
        },
      ],
      baseApiKey: "billingConfigurations",
      dataHeader: ["Transaction Type", "Amt."],
      title: "Billing Configuration",
      link: `${routes.billingConfig}`,
      module: actionsKeys?.BILLING_CONFIG?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "valueObject",
      data: [
        {
          label: "Monthly HOA Fees",
          value: ["0", "0"],
          apiKey: ["monthly", "monthlyAmount"],
          baseApiKey: ["billing", "amount"],
        },
        {
          label: "Yearly HOA Fees",
          value: ["0", "0"],
          apiKey: ["yearly", "yearlyAmount"],
          baseApiKey: ["billing", "amount"],
        },
        {
          label: "Adhoc HOA Fees",
          value: ["0", "0"],
          apiKey: ["adhoc", "adhocAmount"],
          baseApiKey: ["billing", "amount"],
        },
      ],
      baseApiKey: "pendingPosting",
      dataHeader: ["Transaction Type", "Billing", "Amt."],
      title: "Pending For Posting",
      link: `${routes.pendingPosting}`,
      module: actionsKeys?.BILLING_DOCUMENT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "valueObject",
      data: [
        {
          label: "Monthly HOA Fees",
          value: ["0"],
          apiKey: ["monthlyAmount"],
        },
        {
          label: "Yearly HOA Fees",
          value: ["0"],
          apiKey: ["yearlyAmount"],
        },
        {
          label: "Adhoc HOA Fees",
          value: ["0"],
          apiKey: ["adhocAmount"],
        },
      ],
      baseApiKey: "postedBillAmounts",
      dataHeader: ["Transaction Type", "Amt."],
      title: "Billed For the Period",
      link: `${routes.billedForPeriod}`,
      module: actionsKeys?.BILLING_DOCUMENT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    // {
    //   id: "titleOnly",
    //   title: "Unified Journal Entry List",
    //   link: `${routes.journalEntry}`,
    //   module: actionsKeys?.UNIFIED_JE?.toLowerCase(),
    //   accessKeys: permissionActions.READ,
    // },
  ]);

  const [clearingCards, setClearingCards] = useState([
    {
      id: "create",
      title: "Create Clearing Document",
      link: `${routes.clearing}/${routes.add}`,
      module: actionsKeys?.MANUAL_CLEARING?.toLowerCase(),
      accessKeys: permissionActions.CREATE,
    },
    {
      id: "summary",
      data: [
        { label: "Total Cleared Amount", value: 0, apiKey: "clearedAmount" },
        { label: "Pending Clearance", value: 0, apiKey: "pendingAmount" },
        { label: "Total Variance", value: 0, apiKey: "varianceAmount" },
      ],
      title: "Customer Clearing Document",
      link: `${routes.clearing}`,
      subtitle: "",
      subtitleColor: "text-yellow-600",
      module: actionsKeys?.MANUAL_CLEARING?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
  ]);

  const [collectionCards, setCollectionCards] = useState([
    {
      id: "summary",
      data: [
        {
          label: "# Documents",
          value: 0,
          apiKey: "documentCount",
        },
        {
          label: "Total Collected Amount",
          value: 0,
          apiKey: "collectedAmount",
        },
      ],
      baseApiKey: "cashApp",
      dataHeader: [],
      title: "Cash App",
      link: `${routes.cashApp}`,
      module: actionsKeys?.CASH_APP?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "titleOnly",
      title: "Unified Journal Entry List",
      link: `${routes.journalEntry}`,
      module: actionsKeys?.UNIFIED_JE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "reports",
      desc: "Collection 360 Report",
      title: "View Reports",
      icon: FileChartColumn,
      link: `${routes.ar360}`,
      subtitle: "",
      subtitleColor: "text-green-600",
      apiKey: "",
      hideCount: true,
      module: actionsKeys?.COLLECTION_REPORT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "owner-trial-balance",
      title: "View",
      desc: "Owner Trial Balance",
      icon: FileChartColumn,
      link: `${routes.ownerTrialBalance}`,
      subtitle: "",
      hideCount: true,
      apiKey: "draft",
      module: actionsKeys?.OWNER_TRIAL_BALANCE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "report-by-owner",
      title: "View",
      desc: "Report By Owner",
      icon: FileChartColumnIncreasing,
      link: `${routes.reportByOwner}`,
      subtitle: "",
      hideCount: true,
      apiKey: "",
      module: actionsKeys?.OWNER_BALANCE_REPORT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
  ]);

  const [generalLedgerCards, setGeneralLedgerCards] = useState([
    {
      id: "gl-configuration-studio",
      title: "View",
      desc: "GL Configuration Studio",
      icon: FileCog,
      link: `${routes.glConfigurationStudio}`,
      subtitle: "",
      hideCount: true,
      apiKey: "draft",
      module: actionsKeys?.GL_CONFIG?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "fiscal-year-closing",
      title: "View",
      desc: "Fiscal Year Closing",
      icon: FileChartColumnIncreasing,
      link: `${routes.fiscalYearClosing}`,
      subtitle: "",
      hideCount: true,
      apiKey: "",
      module: actionsKeys?.FISCAL_YEAR?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "fiscal-year-closing-lifecycle",
      title: "View",
      desc: "Fiscal Year Lifecycle",
      icon: RefreshCw,
      link: `${routes.fiscalYearLifeCycle}`,
      subtitle: "",
      hideCount: true,
      apiKey: "",
      module: actionsKeys?.FISCAL_YEAR_LIFE_CYCLE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "trial-balance",
      title: "View",
      desc: "GL Trial Balance",
      icon: FileChartColumnIncreasing,
      link: `${routes.trialBalance}`,
      subtitle: "",
      hideCount: true,
      apiKey: "",
      module: actionsKeys?.GL_TRIAL_BALANCE?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "display-accounting-document",
      title: "View",
      desc: "Display Accounting Document",
      icon: FileChartColumnIncreasing,
      link: `${routes.displayAccountingDocument}`,
      subtitle: "",
      hideCount: true,
      apiKey: "",
      module: actionsKeys?.DISPLAY_ACCOUNTING_DOCUMENT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    // {
    //   id: "create-je-manual",
    //   title: "Create",
    //   desc: "Create JE Manual",
    //   icon: FilePen,
    //   link: `${routes.createJEManual}`,
    //   subtitle: "",
    //   hideCount: true,
    //   apiKey: "draft",
    //   module: actionsKeys?.MANUAL_JE?.toLowerCase(),
    //   accessKeys: permissionActions.CREATE,
    // },
    // {
    //   id: "parked-je-manual",
    //   title: "View",
    //   desc: "Parked JE Manual",
    //   icon: FileClockIcon,
    //   link: `${routes.jeManual}/parked`,
    //   subtitle: "",
    //   hideCount: true,
    //   apiKey: "draft",
    //   module: actionsKeys?.MANUAL_JE?.toLowerCase(),
    //   accessKeys: permissionActions.READ,
    // },
    // {
    //   id: "posted-je-manual",
    //   title: "View",
    //   desc: "Posted JE Manual",
    //   icon: FileCheck2,
    //   link: `${routes.jeManual}/posted`,
    //   subtitle: "",
    //   hideCount: true,
    //   apiKey: "draft",
    //   module: actionsKeys?.MANUAL_JE?.toLowerCase(),
    //   accessKeys: permissionActions.READ,
    // },
  ]);

  const [auditTrailCards, setAuditTrailCards] = useState([
    {
      id: "audit-trail",
      title: "View",
      desc: "Audit Trail",
      icon: History,
      link: `${routes.auditTrail}`,
      subtitle: "",
      hideCount: true,
      apiKey: "draft",
      module: actionsKeys?.AUDIT_TRAIL?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "reconciliation-report",
      title: "View",
      desc: "GL/CA Reconciliation Report",
      icon: FileChartColumnIncreasing,
      link: `${routes.reconciliation}`,
      subtitle: "",
      hideCount: true,
      apiKey: "",
      module: actionsKeys?.RECONCILIATION_REPORT?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "distribution-job-configuration",
      title: "View",
      desc: "Allocation Run Time Details",
      icon: Settings,
      link: `${routes.distributionRuntimeDetails}`,
      subtitle: "",
      hideCount: true,
      apiKey: "draft",
      module: actionsKeys?.DISTRIBUTION_CONFIG?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
    {
      id: "summary",
      data: [
        {
          label: "Last Monthly Run",
          value: "",
          apiKey: "monthly",
        },
        {
          label: "Last Yearly Run",
          value: "",
          apiKey: "yearly",
        },
        {
          label: "Last Adhoc Run",
          value: "",
          apiKey: "adhoc",
        },
      ],
      baseApiKey: "lastBillingLogs",
      title: "Billing Log",
      link: `${routes.billingLog}`,
      module: actionsKeys?.BILLING_LOGS?.toLowerCase(),
      accessKeys: permissionActions.READ,
    },
  ]);
  
  const dispatch = useDispatch();

  const communityData = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const currentFinanceYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );

  const activityDashboardOpenTab = useSelector(
    (state: RootState) => state.currentData.activityDashboardOpenTab
  );

  const { data: communityList, isLoading: isCommunityLoading } =
    useGetCommunities({});

  const { data: invoiceCount, isLoading: isInvoiceCountLoading } =
    useGetParkDocStatusCountByCommunityId({
      communityId: communityData?.communityId,
      year: currentFinanceYear,
      queryParams: {
        enabled: !!communityData?.communityId,
      },
    });

  const { data: paymentStat, isLoading: isPaymentStatLoading } =
    useGetPaymentStat({
      communityId: communityData?.communityId,
      queryParams: {
        enabled: !!communityData?.communityId,
      },
    });

  const { data: billingStat, isLoading: isBillingStatLoading } =
    useGetBillingStat({
      communityId: communityData?.communityId,
      billingYear: currentFinanceYear,
      queryParams: {
        enabled: !!communityData?.communityId,
      },
    });

  const { data: clearingStat, isLoading: isClearingStatLoading } =
    useGetClearingStat({
      communityId: communityData?.communityId,
      year: currentFinanceYear,
      queryParams: {
        enabled: !!(communityData?.communityId && accessPermission[actionsKeys?.MANUAL_CLEARING?.toLowerCase()][permissionActions.READ]),
      },
    });

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ["document-type", communityData?.communityId],
    });
  }, []);

  useEffect(() => {
    if (activityDashboardOpenTab != openTabs) {
      setOpenTabs(activityDashboardOpenTab);
    }
  }, [activityDashboardOpenTab]);

  useEffect(() => {
    if (accessPermission) {
      let cards = generalLedgerCards?.map((card: any) => ({
        ...card, 
        access: !!accessPermission?.[card.module]?.[card.accessKeys],
      }))
      setGeneralLedgerCards(cards);
    }
  }, [accessPermission]);

  useEffect(() => {
    if (!billingStat) return;

    const updatedCards = auditTrailCards?.map((card: any) => {
      const { id, data: cardData, baseApiKey, apiKey } = card;
      const statData = (billingStat as any)?.data;

      if (id === "summary") {
        const updatedData = cardData.map((item: any) => ({
          ...item,
          value: item.apiKey?.toLowerCase()?.includes("amount")
            ? formatAmount(statData?.[baseApiKey]?.[item.apiKey])
            : statData?.[baseApiKey]?.[item.apiKey],
        }));

        return {
          ...card,
          data: updatedData,
          access: accessPermission?.[card.module]?.[card.accessKeys],
        };
      }

      return {
        ...card,
        value: statData?.[apiKey] ?? 0,
        access: !!accessPermission?.[card.module]?.[card.accessKeys],
      };
    });
    setAuditTrailCards(updatedCards);
  }, [billingStat, accessPermission]);

  useEffect(() => {
    if (!paymentStat && !invoiceCount) return;

    const statData = {...(paymentStat as any)?.data, ...(invoiceCount as any)?.data};

    const updatedCards = paymentCards.map((card: any) => {
      const { id, data: cardData, apiKey } = card;

      if (id === "summary") {
        const total = Object.values(statData).reduce(
          (acc: number, val: any) => {
            const num = parseFloat(val);
            return acc + (isNaN(num) ? 0 : num);
          },
          0
        );

        const updatedData = cardData.map((item: any) => ({
          ...item,
          value:
            item.apiKey === "total"
              ? total
              : formatAmount(statData?.[item.apiKey] ?? 0),
        }));

        return {
          ...card,
          data: updatedData,
          access: !!accessPermission?.[card.module]?.[card.accessKeys],
        };
      }

      return {
        ...card,
        count: card?.onlyCount ? statData?.[apiKey] ?? 0 : formatAmount(statData?.[apiKey] ?? 0),
        access: !!accessPermission?.[card.module]?.[card.accessKeys],
      };
    });

    setPaymentCards(updatedCards);
  }, [paymentStat, accessPermission, communityData?.communityId, invoiceCount]);

  useEffect(() => {
    if (!invoiceCount) return;

    const rawData = (invoiceCount as any)?.data || {};

    const statData: any = Object.fromEntries(
      Object.entries(rawData).filter(([key]) => key !== "vpApproved")
    );

    const updatedCards = invoiceCards.map((card: any) => {
      const { id, data: cardData, apiKey } = card;

      if (id === "summary") {
        const total = Object.values(statData).reduce(
          (acc: number, val: any) => {
            const num = parseFloat(val);
            return acc + (isNaN(num) ? 0 : num);
          },
          0
        );

        const updatedData = cardData.map((item: any) => ({
          ...item,
          value:
            item.apiKey === "total"
              ? total
              : item.apiKey == "pendingForApproval"
              ? statData["pendingForApproval"] +
                statData["pendingForPMApproval"]
              : statData?.[item.apiKey] ?? 0,
        }));

        return {
          ...card,
          data: updatedData,
          access: !!accessPermission?.[card.module]?.[card.accessKeys],
        };
      }

      return {
        ...card,
        count: statData?.[apiKey] ?? 0,
        access: !!accessPermission?.[card.module]?.[card.accessKeys],
      };
    });

    setInvoiceCards(updatedCards);
  }, [invoiceCount, accessPermission, communityData?.communityId]);

  useEffect(() => {
    if (!billingStat) return;

    const updatedCards = billingCards.map((card: any) => {
      const { id, data: cardData, baseApiKey, apiKey } = card;
      const statData = (billingStat as any)?.data;

      if (id === "summary") {
        const updatedData = cardData.map((item: any) => ({
          ...item,
          value: item.apiKey?.toLowerCase()?.includes("amount")
            ? formatAmount(statData?.[baseApiKey]?.[item.apiKey])
            : statData?.[baseApiKey]?.[item.apiKey],
        }));

        return {
          ...card,
          data: updatedData,
          access: accessPermission?.[card.module]?.[card.accessKeys],
        };
      }

      if (id === "valueObject") {
        const updatedData = cardData.map((item: any) => {
          const { apiKey: keys, baseApiKey: baseKeys } = item;

          const values = keys.map((key: string, index: number) => {
            const baseKey = baseKeys?.[index];

            const rawValue = baseKey
              ? statData?.[baseApiKey]?.[baseKey]?.[key]
              : statData?.[baseApiKey]?.[key];

            return baseKey?.toLowerCase()?.includes("amount") ||
              key?.toLowerCase()?.includes("amount")
              ? formatAmount(rawValue ?? 0)
              : rawValue ?? 0;
          });

          return {
            ...item,
            value: values,
            access: !!accessPermission?.[card.module]?.[card.accessKeys],
          };
        });

        return {
          ...card,
          data: updatedData,
          access: !!accessPermission?.[card.module]?.[card.accessKeys],
        };
      }

      return {
        ...card,
        value: statData?.[apiKey] ?? 0,
        access: !!accessPermission?.[card.module]?.[card.accessKeys],
      };
    });

    setBillingCards(updatedCards);

    // for collection
    const collection = collectionCards.map((card: any) => {
      const { id, data: cardData, baseApiKey, apiKey } = card;
      const statData = (billingStat as any)?.data;

      if (id === "summary") {
        const updatedData = cardData.map((item: any) => {
          if (item.apiKey?.toLowerCase()?.includes("total")) {
            return ({
              ...item,
              value: formatAmount(Number(statData?.[baseApiKey]?.collectedAmountParked + statData?.[baseApiKey]?.collectedAmountPosted))
            })
          }
          return ({
            ...item,
            value: item.apiKey?.toLowerCase()?.includes("amount")
              ? formatAmount(statData?.[baseApiKey]?.[item.apiKey])
              : statData?.[baseApiKey]?.[item.apiKey],
          })
        });

        return {
          ...card,
          data: updatedData,
          access: accessPermission?.[card.module]?.[card.accessKeys],
        };
      }

      if (id === "valueObject") {
        const updatedData = cardData.map((item: any) => {
          const { apiKey: keys, baseApiKey: baseKeys } = item;

          const values = keys.map((key: string, index: number) => {
            const baseKey = baseKeys?.[index];

            const rawValue = baseKey
              ? statData?.[baseApiKey]?.[baseKey]?.[key]
              : statData?.[baseApiKey]?.[key];

            return baseKey?.toLowerCase()?.includes("amount") ||
              key?.toLowerCase()?.includes("amount")
              ? formatAmount(rawValue ?? 0)
              : rawValue ?? 0;
          });

          return {
            ...item,
            value: values,
            access: !!accessPermission?.[card.module]?.[card.accessKeys],
          };
        });

        return {
          ...card,
          data: updatedData,
          access: !!accessPermission?.[card.module]?.[card.accessKeys],
        };
      }

      return {
        ...card,
        value: statData?.[apiKey] ?? 0,
        access: !!accessPermission?.[card.module]?.[card.accessKeys],
      };
    });

    setCollectionCards(collection);
  }, [billingStat, accessPermission, communityData?.communityId]);

  useEffect(() => {
    if (!clearingStat) return;

    const statData = (clearingStat as any)?.data;

    const updatedCards = clearingCards.map((card: any) => {
      const { id, data: cardData, apiKey } = card;

      if (id === "summary") {
        const total = Object.values(statData || {})?.reduce(
          (acc: number, val: any) => {
            const num = parseFloat(val);
            return acc + (isNaN(num) ? 0 : num);
          },
          0
        );

        const updatedData = cardData.map((item: any) => ({
          ...item,
          value:
            item.apiKey === "total"
              ? total
              : formatAmount(statData?.[item.apiKey] ?? 0),
        }));

        return {
          ...card,
          data: updatedData,
          access: !!accessPermission?.[card.module]?.[card.accessKeys],
        };
      }

      return {
        ...card,
        count: formatAmount(statData?.[apiKey] ?? 0),
        access: !!accessPermission?.[card.module]?.[card.accessKeys],
      };
    });

    setClearingCards(updatedCards);
  }, [clearingStat, communityData?.communityId]);

  useEffect(() => {
    if (communityList) {
      if (communityData) {
        setCommunityId(communityData?.communityId?.toString());
        setCommunityKey(Math.random() * 2);
      } else {
        setCommunityId(communityList?.data?.[0]?.communityId?.toString());
        dispatch(setCommunityData(communityList?.data?.[0]));
        setCommunityKey(Math.random() * 2);
      }
    }
  }, [communityList]);

  useEffect(() => {
    if (currentFinanceYear) {
      setYear(currentFinanceYear);
      setYearKey(Math.random() * 3);
    } else {
      dispatch(setCurrentFinanceYear(moment().format("YYYY")));
      setYearKey(Math.random() * 3);
    }
  }, []);

  const onCommunityChange = (e: any) => {
    setCommunityId(e);
    const details = communityList?.data?.find(
      (data: any) => data.communityId == e
    );
    dispatch(setCommunityData(details));
  };

  const onYearChange = (e: any) => {
    dispatch(setCurrentFinanceYear(e));
    setYear(e);
  };

  const onAccordionChange = (e: any) => {
    setOpenTabs([e[e.length - 1]]);
    dispatch(setActivityDashboardOpenTab([e[e.length - 1]] as any));
  };

  const onExpandCollapse = () => {
    if (openTabs?.length) {
      setOpenTabs([]);
      dispatch(setActivityDashboardOpenTab([] as any));
    } else {
      setOpenTabs([PAYMENT, INVOICE, BILLING, COLLECTION, CLEARING, GL, AUDIT_TRAIL, COST]);
      dispatch(
        setActivityDashboardOpenTab([PAYMENT, INVOICE, BILLING, COLLECTION, CLEARING, GL, AUDIT_TRAIL, COST] as any)
      );
    }
  };

  const onCreateCardClick = (link: string) => () => {
    if (communityData) {
      dispatch(setInvoiceIds(null));
      dispatch(setPaymentIds(null));
      dispatch(setInvoiceFrom(""));
      dispatch(setGls(null));
      navigate(link);
    } else {
      toast.error("Please select community");
    }
  };

  const onCardClick = (link: string) => () => {
    dispatch(setInvoiceIds(null));
    dispatch(setPaymentIds(null));
    dispatch(setInvoiceFrom(""));
    dispatch(setGls(null));
    dispatch(setInvoiceDueDate(null));
    navigate(link);
  };

  return {
    communityList: communityList?.data || [],
    isCommunityLoading,
    onCommunityChange,
    communityId,
    communityData,
    year,
    onYearChange,
    invoiceCards,
    paymentCards,
    onAccordionChange,
    openTabs,
    onExpandCollapse,
    onCreateCardClick,
    communityKey,
    onCardClick,
    isInvoiceCountLoading,
    yearKey,
    isPaymentStatLoading,
    billingCards,
    isBillingStatLoading,
    collectionCards,
    clearingCards,
    isClearingStatLoading,
    generalLedgerCards,
    auditTrailCards
  };
};

export default useActivityDashboard;
